import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AnimeCard from '@/components/feature/AnimeCard';
import FaqAccordion from '@/components/feature/FaqAccordion';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categoryInfo, getFeaturedAnime, getLatestUpdates, faqData, statsData, testimonials } from '@/mocks/animeData';

const featuredAnime = getFeaturedAnime();
const latestAnime = getLatestUpdates();

export default function Home() {
  return (
    <>
      <ScrollToTop />
      {/* SEO Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: '51动漫',
        alternateName: '51动漫网',
        url: 'https://51anime.com',
        description: '51动漫品牌网站导航，提供好看的动漫大全、海量韩漫日漫国漫免费全集，支持追更下拉式阅读。',
        inLanguage: 'zh-CN',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://51anime.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '51动漫',
        url: 'https://51anime.com',
        logo: 'https://51anime.com/logo.png',
        description: '提供海量韩漫、日漫、国漫免费全集在线观看的动漫平台。',
        sameAs: ['https://weibo.com/51anime', 'https://space.bilibili.com/51anime'],
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      }) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20anime%20style%20illustration%20with%20colorful%20manga%20panels%20and%20dynamic%20character%20silhouettes%2C%20warm%20orange%20and%20coral%20gradient%20sky%2C%20abstract%20geometric%20shapes%2C%20modern%20minimalist%20design%2C%20Japanese%20and%20Korean%20comic%20aesthetic%20elements%2C%20energetic%20composition%2C%20soft%20lighting%2C%20clean%20digital%20art&width=1920&height=800&seq=hero-bg-anime&orientation=landscape"
            alt="51动漫 - 海量动漫免费全集"
            title="51动漫 - 好看的动漫大全"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/50 via-background-950/30 to-background-950/60" />
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
              发现你的下一个
              <span className="text-primary-400">最爱动漫</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              51动漫网汇聚海量韩漫、日漫、国漫免费全集，支持追更下拉式阅读，随时随地畅享精彩动漫世界。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/korean"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                开始探索 <i className="ri-arrow-right-line ml-1" />
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/15 hover:bg-background-50/25 text-background-50 font-medium rounded-full text-sm backdrop-blur-sm transition-all duration-300 whitespace-nowrap cursor-pointer border border-background-50/20"
              >
                了解更多
              </Link>
            </div>
          </div>
        </header>

        <main>
          {/* Stats Section */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {statsData.map((stat) => (
                  <div key={stat.label} className="text-center p-4 md:p-6">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${stat.icon} text-primary-600 text-lg`} />
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{stat.value}</div>
                    <div className="text-foreground-500 text-sm mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Category Section */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  探索动漫分类
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                  韩漫的精致唯美、日漫的经典热血、国漫的东方神韵，总有一款打动你
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(categoryInfo).map(([key, cat]) => (
                  <Link
                    key={key}
                    to={`/category/${cat.slug}`}
                    className="group relative bg-background-50 rounded-lg border border-background-200/70 overflow-hidden p-6 md:p-8 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 mb-5 group-hover:bg-accent-200 transition-colors">
                      <i className={`${cat.icon} text-accent-600 text-2xl`} />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">{cat.name}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed mb-4">{cat.desc}</p>
                    <span className="inline-flex items-center gap-1 text-primary-600 text-sm font-medium group-hover:gap-2 transition-all whitespace-nowrap">
                      浏览{cat.name} <i className="ri-arrow-right-line text-xs" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Anime Section */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-end justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    高分推荐
                  </h2>
                  <p className="text-foreground-500 text-sm">评分最高的热门动漫精选</p>
                </div>
                <Link to="/category/korean" className="hidden sm:flex items-center gap-1 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors whitespace-nowrap">
                  查看全部 <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {featuredAnime.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates Section */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-end justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    最近更新
                  </h2>
                  <p className="text-foreground-500 text-sm">追更党必看，最新连载一话不漏</p>
                </div>
                <Link to="/category/japanese" className="hidden sm:flex items-center gap-1 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors whitespace-nowrap">
                  查看全部 <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {latestAnime.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-foreground-500 text-sm max-w-xl mx-auto">
                  听听动漫爱好者们怎么说
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t, i) => (
                  <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm" />
                      ))}
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4">&ldquo;{t.comment}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                      <span className="font-medium text-foreground-900 text-sm">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-foreground-500 text-sm">关于51动漫网的使用指南</p>
              </div>
              <FaqAccordion items={faqData} />
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 md:py-24 relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20gradient%20abstract%20background%20with%20soft%20coral%20and%20orange%20tones%2C%20flowing%20organic%20shapes%2C%20Japanese%20ink%20wash%20inspired%20texture%2C%20modern%20minimalist%20design%2C%20gentle%20atmospheric%20feel%2C%20clean%20composition&width=1920&height=500&seq=cta-bg-anime&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-primary-900/80" />
            <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
                准备好开始你的动漫之旅了吗？
              </h2>
              <p className="text-background-200 text-sm md:text-base mb-8">
                海量韩漫日漫国漫免费全集，支持追更下拉式阅读，就在51动漫网。
              </p>
              <Link
                to="/category/korean"
                className="inline-flex items-center gap-2 px-8 py-3 bg-accent-500 hover:bg-accent-400 text-background-50 font-medium rounded-full text-sm transition-all whitespace-nowrap cursor-pointer"
              >
                立即探索 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}